import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { endOfMonth, endOfWeek, endOfYear, format, formatISO, getISOWeek, isAfter, isBefore, isSameDay, isSameMinute, parseISO, startOfMonth, startOfWeek, startOfYear, sub } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { Observable, takeUntil } from 'rxjs';
import { ForecastMeta } from 'src/app/interfaces/api/chart';
import { ChartHandlerV2 } from 'src/app/classes/widget/chartHandlerV2';
import { ForecastNoteComponent } from 'src/app/components/project-pages/dashboard/forecast-note/forecast-note.component';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';
import { migrateBlockColors } from './block-colors';
import { Landing, LandingDesign, LandingPeriod, landingQuery, landingReference, landingValue, periodBounds, previousTotal } from './landing';

export type TrendTone = 'good' | 'critical' | 'neutral';

export interface KpiTrend {
  symbol: '▲' | '▼' | '=' | '—';
  percent: number | null;     // signed, null without a usable previous value
  tone: TrendTone;
}

// The icon of each trend symbol, none without a previous value.
const TREND_ICONS: Record<KpiTrend['symbol'], string | null> = { '▲': 'trending_up', '▼': 'trending_down', '=': 'trending_flat', '—': null };

// The value a block shows: the first row, the last element when it is a list.
export function kpiValue(rows: any[] | null | undefined): number | null {
  let y = rows?.[0]?.y;
  if (Array.isArray(y)) y = y.at(-1);
  const value = Number(y);
  return y === undefined || y === null || y === '' || !Number.isFinite(value) ? null : value;
}

// Change against the previous period; neutral "—" without a previous value or when it is 0.
export function kpiTrend(current: number | null, previous: number | null, higherIsWorse = false): KpiTrend {
  if (current === null || previous === null || previous === 0) return { symbol: '—', percent: null, tone: 'neutral' };
  if (current === previous) return { symbol: '=', percent: 0, tone: 'neutral' };

  const up = current > previous;
  const good = up !== higherIsWorse;
  return { symbol: up ? '▲' : '▼', percent: (current - previous) / Math.abs(previous) * 100, tone: good ? 'good' : 'critical' };
}

// The window compared against, in words: "1 sept. – 30 sept." (with the year when it is not this one), or one day
// and its hours; null without one.
export function periodText(from: string | undefined, to: string | undefined, lang: string, now: Date = new Date()): string | null {
  if (!from || !to) return null;

  const start = new Date(from);
  const end = new Date(new Date(to).getTime() - 1);
  const locale = lang.startsWith('fr') ? fr : enUS;
  const thisYear = start.getFullYear() === now.getFullYear() && end.getFullYear() === now.getFullYear();
  const day = thisYear ? 'd MMM' : 'd MMM yyyy';
  const whole = (from: Date, to: Date) => isSameMinute(start, from) && isSameMinute(end, to);

  if (whole(startOfYear(start), endOfYear(start)))
    return format(start, 'yyyy');

  if (whole(startOfMonth(start), endOfMonth(start)))
    return format(start, thisYear ? 'MMMM' : 'MMMM yyyy', { locale });

  if (whole(startOfWeek(start, { weekStartsOn: 1 }), endOfWeek(start, { weekStartsOn: 1 })))
    return `${lang.startsWith('fr') ? 'S' : 'W'}${getISOWeek(start)}`;

  if (isSameDay(start, end)) {
    const wholeDay = format(start, 'HH:mm') === '00:00' && format(end, 'HH:mm') === '23:59';
    return wholeDay ? format(start, day, { locale }) : `${format(start, day, { locale })} ${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`;
  }

  return `${format(start, day, { locale })} – ${format(end, day, { locale })}`;
}

// Rounded to the unit, to 0.1 under 10 %.
export function formatTrendPercent(percent: number, locale: string): string {
  const value = Math.abs(percent);
  return new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value);
}

// A plain number with its thousands grouped ("12345.5" → "12 345,5"), with the same decimals; anything else as it is.
export function groupDigits(value: string | number, locale: string): string {
  const text = `${value}`;
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;

  const decimals = text.split('.')[1]?.length ?? 0;
  return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Number(text));
}

@Component({
  selector: 'app-text',
  imports: [
    ForecastNoteComponent,
    WidgetLoadingComponent,
    CommonModule,
    MatIconModule,
    TranslateModule
  ],
  standalone: true,
  templateUrl: './text.component.html',
  styleUrl: './text.component.scss'
})

export class TextComponent extends WidgetDirective {
  // Variables spécifiques à TextComponent
  blockValue: any = "";
  // The suffix of the format, shown smaller next to the value ("12 t").
  unit: string = "";
  chartHandler: ChartHandlerV2 = new ChartHandlerV2();
  private readonly translate: TranslateService = inject(TranslateService);

  // The tile: its colors (null: the theme ones), its accent bar and its color (the alert one under conditional
  // formatting, null: the theme one), its text.
  background: string | null = null;
  color: string | null = null;
  showAccent: boolean = true;
  accent: string | null = null;
  alert: boolean = false;
  icon: string = "";
  description: string = "";
  titleOnTop: boolean = true;
  align: string = "center";
  titleSize: number = 14;
  valueSize: number = 40;

  // The landing of the period (design.landing), under the value: its own query (the block's, with the dashboard's
  // filters) as a daily series and its forecast to the end of the period; one per project in a project dashboard,
  // added up. Compared with the whole previous period when the comparison is on.
  landing: Landing | null = null;
  landingMeta: ForecastMeta | null = null;
  landingEnd: string = "";
  landingValueText: string = "";
  landingTrend: KpiTrend | null = null;
  landingTrendIcon: string | null = null;
  landingTrendPercent: string = "";
  landingPrevious: string = "";
  landingDetails: string = "";
  // The day it is computed for: the end of the dashboard's date window when it is past.
  private landingAt: Date = new Date();
  private landingRows: Record<string, any[]> = {};
  private landingMetas: Record<string, ForecastMeta> = {};
  private valueFormat: any = null;

  // Compared to the previous period (query.compare.previous), null when the option is off.
  trend: KpiTrend | null = null;
  trendIcon: string | null = null;
  trendPercent: string = "";
  previousValue: string = "";
  previousPeriod: string | null = null;

  private get locale(): string {
    return this.translate.currentLang || 'fr-FR';
  }

  // Implémentation de la méthode abstraite
  init(): void {
    if (!this.widget || !this.widget.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;
    this.widgetQuery = this.widget.query;
    this.type = this.widget.type;
    this.initOptions();
    this.updateBlock();
  }

  initOptions(): void {
    try {
      migrateBlockColors(this.widgetDesign);
      const colors = this.widgetDesign?.colors;
      const text = this.widgetDesign?.text;
      this.alert = !!colors?.conditionalFormatting && this.isAlt(colors);

      this.background = (this.alert ? colors.altBackgroundColor : colors?.backgroundColor) || null;
      this.color = (this.alert ? colors.altTextColor : colors?.textColor) || null;
      this.showAccent = colors?.accent === true;
      this.accent = (this.alert ? colors.altTextColor : colors?.accentColor) || null;

      this.showTitle = text?.display ?? true;
      this.titleOnTop = (text?.position || 'top') == 'top';
      this.align = text?.align || 'center';
      this.titleSize = Number(text?.titleFontSize) || 14;
      this.valueSize = Number(text?.valueFontSize) || 40;
      this.icon = text?.icon || "";
      this.description = text?.description?.trim() || "";
    } catch (e: any) {
      console.error("Error initializing widget options:", e);
    }
  }

  // Whether the value meets the condition of the conditional formatting.
  private isAlt(colors: any): boolean {
    if (colors.type != "Date" && colors.type != "Period") return this.isAltValue(colors.operator, colors.value);

    if (colors.type == "Date") return this.isAltDate(colors.operator, colors.date);

    const duration: any = {};
    duration[colors.periodValue] = colors.periodAmount;
    return this.isAltDate(colors.operator, formatISO(sub(new Date(), duration)));
  }

  get landingDesign(): LandingDesign | null {
    const design: LandingDesign | undefined = this.widget?.design?.landing;
    return design?.enabled ? design : null;
  }

  override updateData(): void {
    this.landingRows = {};
    this.landingMetas = {};
    super.updateData();
  }

  // The block's query as it is; with a landing, its second query, with the same filters.
  protected override aggregate(query: any, label?: string): Observable<any[]> {
    const landing = this.landingDesign;
    if (landing) this.loadLanding(query, landing.period ?? 'month', label ?? '');
    return super.aggregate(query, label);
  }

  private loadLanding(query: any, period: LandingPeriod, key: string): void {
    this.landingAt = landingReference(query);
    this.request(landingQuery(query, period))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.landingRows[key] = Array.isArray(result) ? result : result?.rows ?? [];
          if (result?.meta?.forecast) this.landingMetas[key] = result.meta.forecast;
          this.updateLanding();
          this.cdr.detectChanges();
        },
        error: () => this.landing = null,
      });
  }

  private updateLanding(): void {
    const design = this.landingDesign;
    const keys = Object.keys(this.landingRows);

    if (!design || !keys.length || !this.valueFormat) {
      this.landing = null;
      return;
    }

    const period = design.period ?? 'month';
    const rows = keys.flatMap(key => this.landingRows[key] ?? []);
    const metas = Object.values(this.landingMetas);
    const text = (value: number) => groupDigits(String(this.chartHandler.formatValue(this.valueFormat, value)), this.locale) + (this.unit ? ' ' + this.unit : '');
    const span = (start: Date, end: Date) => periodText(start.toISOString(), new Date(end.getTime() + 1).toISOString(), this.locale) ?? '';
    const { start, end } = periodBounds(period, this.landingAt);

    this.landing = landingValue(rows, period, this.landingAt);
    this.landingMeta = metas.length ? { ...metas[0]!, series: metas.flatMap(meta => meta.series) } : null;
    this.landingEnd = span(start, end);
    this.landingValueText = this.landing ? text(this.landing.value) : "";

    const details = this.landing ? [this.translate.instant('editor.landing_done', { value: text(this.landing.actual) })] : [];
    if (this.landing?.lower !== null && this.landing?.lower !== undefined)
      details.push(this.translate.instant('editor.landing_range', { range: `${text(this.landing.lower)} – ${text(this.landing.upper!)}` }));

    this.landingTrend = null;
    if (this.landing && this.widgetQuery?.compare?.previous) {
      const previous = previousTotal(rows, period, this.landingAt);
      this.landingTrend = kpiTrend(this.landing.value, previous.total, !!this.widgetDesign?.trend?.higherIsWorse);
      this.landingTrendIcon = TREND_ICONS[this.landingTrend.symbol];
      this.landingTrendPercent = this.landingTrend.percent === null ? "" : formatTrendPercent(this.landingTrend.percent, this.locale);
      this.landingPrevious = span(previous.start, previous.end);
      details.push(`${this.landingPrevious} : ${text(previous.total)}`);
    }

    this.landingDetails = details.join(' · ');
  }

  updateBlock(): void {
    this.widgetDesign.format = this.widgetDesign[(this.widgetDesign.text.format || 'text') + 'Format'];
    this.widgetDesign.format.type = this.widgetDesign.text.format;

    // The suffix apart: the unit next to the value.
    const format = this.widgetDesign.format;
    this.unit = format.display && format.suffix ? `${format.suffix}`.trim() : "";
    const valueFormat = this.unit ? { ...format, suffix: '' } : format;

    this.valueFormat = valueFormat;

    if (this.widgetData && this.widgetData[0] && this.widgetData[0].y) {
      let y = this.widgetData[0].y;

      if (Array.isArray(y)) {
        y = y[y.length - 1];
      }

      this.blockValue = this.chartHandler.formatValue(valueFormat, y);
    } else {
      let placeholder = this.chartHandler.formatValue(valueFormat, 0);

      if (format?.placeholder && format.placeholder != "No data yet...") {
        placeholder = format.placeholder;
        this.unit = "";
      }

      this.blockValue = placeholder;
    }

    this.blockValue = groupDigits(this.parseBlockValue(), this.locale);
    this.updateTrend();
    this.updateLanding();
    this.cdr.detectChanges();
    this.widgetRendered = true;
  }

  private updateTrend(): void {
    if (!this.widgetQuery?.compare?.previous) {
      this.trend = null;
      return;
    }

    // An empty previous period of a count or a sum is 0, not unknown.
    const aggregate = (Object.values(this.widgetQuery?.aggregation ?? {})[0] as any)?.options?.aggregate;
    const empty = this.previousMeta?.rows && ['count', 'sum'].includes(aggregate) ? 0 : null;
    const previous = kpiValue(this.previousMeta?.rows) ?? empty;
    this.setTrend(kpiValue(this.widgetData), previous);
    this.previousPeriod = periodText(this.previousMeta?.from, this.previousMeta?.to, this.locale);
  }

  private setTrend(current: number | null, previous: number | null): void {
    this.trend = kpiTrend(current, previous, !!this.widgetDesign?.trend?.higherIsWorse);
    this.trendIcon = TREND_ICONS[this.trend.symbol];
    this.trendPercent = this.trend.percent === null ? "" : formatTrendPercent(this.trend.percent, this.locale);
    this.previousValue = previous === null ? "" : `${this.chartHandler.formatValue(this.widgetDesign.format, previous)}`;
  }

  parseBlockValue(): string | number {
    if (typeof this.blockValue == "number")
      return this.blockValue;

    if (this.isShortValue())
      return this.blockValue.toString().replace(/,(\S)/g, ", $1");

    return this.blockValue.toString().substring(0, 75).replace(/,(\S)/g, ", $1") + "...";
  }

  isShortValue(blockValue: string | number | null = null): boolean {
    blockValue = blockValue ?? this.blockValue;
    if (blockValue === null || blockValue === undefined) return false;

    return blockValue.toString().length <= 75;
  }

  isAltValue(operator: any, value: number): boolean {
    let isAltValue = false;
    let blockValue: any = "0";

    if (this.widgetData && this.widgetData[0] && this.widgetData[0].y) {
      blockValue = this.widgetData[0].y;
    } else if (this.widgetDesign.format && this.widgetDesign.format.placeholder) {
      blockValue = this.widgetDesign.format.placeholder;
    }

    if (typeof blockValue == "string") {
      blockValue = parseInt(blockValue, 10);
    }

    if (isNaN(blockValue)) return false;

    switch (operator) {
      case "Equal to":
        isAltValue = value == blockValue;
        break;
      case "Not equal to":
        isAltValue = value !== blockValue;
        break;
      case "Greater than":
        isAltValue = value < blockValue;
        break;
      case "Greater than or equal to":
        isAltValue = value <= blockValue;
        break;
      case "Less than":
        isAltValue = value > blockValue;
        break;
      case "Less than or equal to":
        isAltValue = value >= blockValue;
        break;
    }
    return isAltValue;
  }

  isAltDate(operator: any, value: string | Date): boolean {
    let isAltValue = false;
    let blockValue: any = "0";

    if (this.widgetData && this.widgetData[0] && this.widgetData[0].y) {
      blockValue = this.widgetData[0].y;
    } else if (this.widgetDesign.format && this.widgetDesign.format.placeholder) {
      blockValue = this.widgetDesign.format.placeholder;
    }

    if (Array.isArray(blockValue)) {
      blockValue = blockValue.pop();
    }

    const dateValue = typeof value === 'string' ? parseISO(value) : value;
    const dateBlock = typeof blockValue === 'string' ? parseISO(blockValue) : new Date(blockValue);

    switch (operator) {
      case "Equal to":
        isAltValue = isSameDay(dateValue, dateBlock);
        break;
      case "Not equal to":
        isAltValue = !isSameDay(dateValue, dateBlock);
        break;
      case "Greater than":
        isAltValue = isBefore(dateValue, dateBlock);
        break;
      case "Greater than or equal to":
        isAltValue = !isAfter(dateValue, dateBlock);
        break;
      case "Less than":
        isAltValue = isAfter(dateValue, dateBlock);
        break;
      case "Less than or equal to":
        isAltValue = !isBefore(dateValue, dateBlock);
        break;
    }
    return isAltValue;
  }
}
